import { AddInMode, getAddinModeFromUrl } from "../common/utils";
import {
  AddCategoriesToMasterTable,
  addCategoryToItem,
  AddHeaders,
  batchCustomProperties,
  generateCustomPropertyOperations,
  getCustomPropertyValue,
  GetHeaders,
  getOutlookClientHost,
  isConnectedToM365Exchange,
  OutlookClientHost,
  removeCategoryFromItem,
  RemoveHeaders,
  RemoveItemProperties,
  removeNotification,
  setCustomProperty,
  showNotification,
  updateItemProperties,
} from "../common/office-utils";
import { AccountManager } from "./authConfig";
import {
  appendMessageCategoriesViaGraph,
  deleteMessageCategoriesViaGraph,
  getGraphRestIdFromEwsId,
} from "./msgraph-helper";

declare const fabric: any;

interface EncryptionOption {
  Encryption: boolean;
  EncryptionMethod: EncryptionType;
  SharedPassphrase: string;
  PasswordHint: string;
  ExpirationPeriod: number;
  ReadReceipt: boolean;
  EncryptionConfirmation: boolean;
}

enum EncryptionType {
  StandardEncryption = 0,
  SharedPassphraseEncryption = 1,
}

interface EditPreferenceOption {
  SelectedLanguage: LanguageOption;
  categorize: boolean;
  mobileNumber: boolean;
  Logging: Logging;
}

enum LanguageOption {
  en = "en",
  de = "de",
  es = "es",
}

enum Logging {
  Error = "Error",
  Warning = "Warning",
  Debug = "Debug",
  Disabled = "Disabled",
}

let language = require("../../src/assets/i18n/compose-traslation.json");
let defaultLanguage = language["en"];
let showMobileNumberCheckbox = false;
let itemID = "";
let isMailboxAPI_1_8_Available = false;
let savedLabel = false;
const accountManager = new AccountManager();
let addinMode: AddInMode;
let clientHost: OutlookClientHost;
let isM365Exchange = false;
let accessToken = "";
let graphRestId = "";

// Initialize Office when ready
Office.onReady((info) => {
  // Show the spinner before the Office app is ready
  console.log("Entered onReady.");
  ShowSpinnerAndHideContent();
  console.log("Spinner displayed.");

  if (info.host === Office.HostType.Outlook) {
    isMailboxAPI_1_8_Available = Office.context.requirements.isSetSupported("Mailbox", "1.8");
    console.log("Office Outlook loaded.");

    // Save the item first to ensure itemId is assigned OR Retrieve from CustomProperties
    Office.context.mailbox.item.saveAsync(async (saveResult) => {
      if (saveResult.status === Office.AsyncResultStatus.Succeeded) {
        console.log("Item saved as draft successfully.", saveResult.value);
        itemID = saveResult.value;
        console.log("ItemID retrieved: ", itemID);
        await setCustomProperty("ItemID", itemID);
        InitializeComposeTaskpane();
      } else {
        getCustomPropertyValue("ItemID")
          .then((propertyValue) => {
            console.log("ItemID:", propertyValue);
            itemID = propertyValue;
            InitializeComposeTaskpane();
          })
          .catch((error) => {
            console.error("Error getting ItemID from custom properties: ", error);
          });
        console.error("Failed to save item as draft:", saveResult.error);
      }
    });
  }
});

async function InitializeComposeTaskpane() {
  // Get current Flavour of Add-in installed
  addinMode = await getAddinModeFromUrl();
  isM365Exchange = isConnectedToM365Exchange();

  clientHost = await getOutlookClientHost();
  console.log(`Add-in is running on: ${clientHost}`);

  if (isM365Exchange) {
    // Initialize MSAL.
    await accountManager.initialize();

    // Specify minimum scopes for the token needed.
    accessToken = await accountManager.ssoGetAccessToken(["Mail.ReadWrite"]);

    // Convert EWS itemID to Graph REST ID
    graphRestId = await getGraphRestIdFromEwsId(accessToken, itemID);
  }

  const encryptionOption = await InitializeTaskpaneComponents(itemID);

  InitializeUIFromOptions(encryptionOption);
  AddEventListeners();

  const settings = Office.context.roamingSettings;
  const preferredLanguage = settings.get("Language") ?? LanguageOption.en;
  console.log("preferred language: ", preferredLanguage);
  defaultLanguage = language[preferredLanguage];
  loadLocalization();
  setMobileNumberCheckboxVisibility(false);

  // Adjust height in .ms-Dialog.is-open in CSS to display the Logging dropdown
  setLoggingLevelVisibility(false);

  // setCategorizationVisibility(isM365Exchange);

  // Now that the content is loaded, hide the spinner and show the UI
  HideSpinnerAndShowContent();
}

function ShowSpinnerAndHideContent() {
  // Show the spinner
  document.getElementById("spinnerContainer").style.display = "block";

  // Hide the toggle button, dropdown, and more options
  document.getElementById("encryption-toggle-container").style.display = "none";
  document.getElementById("more-options-container").style.display = "none";
  document.getElementById("footer-container").style.display = "none";
  // Initialize the Spinner element using Fabric UI
  var SpinnerElements = document.querySelectorAll(".ms-Spinner");
  for (var i = 0; i < SpinnerElements.length; i++) {
    new fabric["Spinner"](SpinnerElements[i]);
  }
}

// Function to hide spinner and show content
function HideSpinnerAndShowContent() {
  // Hide the spinner
  document.getElementById("spinnerContainer").style.display = "none";

  // Show the toggle button, dropdown, and more options
  document.getElementById("encryption-toggle-container").style.display = "block";
  if (addinMode === AddInMode.Advanced || addinMode === AddInMode.OnPrem) {
    document.getElementById("more-options-container").style.display = "block";
  }

  document.getElementById("footer-container").style.display = "block";
}

async function updateEncryptionOptionsFromHeaders(
  headers: { [key: string]: string },
  encryptionOption: EncryptionOption
) {
  if (headers["x-pgp-encrypt"] === "true") {
    encryptionOption.Encryption = true;

    const encryptionType = headers["x-pgp-encryption-type"];

    if (encryptionType === "pdf") {
      encryptionOption.EncryptionMethod = EncryptionType.SharedPassphraseEncryption;
      // Ensure string assignment, defaulting to empty string if header is missing or undefined,
      // as EncryptionOption.SharedPassphrase and EncryptionOption.PasswordHint expect strings.
      encryptionOption.SharedPassphrase = headers["x-pgp-pdf-passphrase"] || "";
      encryptionOption.PasswordHint = headers["x-pgp-pdf-passphrase-hint"] || "";
    } else if (encryptionType === "standard") {
      encryptionOption.EncryptionMethod = EncryptionType.StandardEncryption;
      // Note: If switching from PDF to Standard, SharedPassphrase/PasswordHint fields
      // in encryptionOption will retain their previous values unless explicitly cleared here.
      // This is generally fine as they are not used by StandardEncryption.
    }
    // If encryptionType is neither "pdf" nor "standard", EncryptionMethod remains
    // unchanged from its value prior to this function call (e.g., from roaming settings or initial defaults).

    if (addinMode === AddInMode.Advanced || addinMode === AddInMode.OnPrem) {
      const expirationPeriodHeader = headers["x-pgp-expiration-period"];
      // Only update ExpirationPeriod if the header is present and represents a valid number.
      if (expirationPeriodHeader !== undefined) {
        const expirationPeriod = parseInt(expirationPeriodHeader, 10);
        if (!isNaN(expirationPeriod)) {
          encryptionOption.ExpirationPeriod = expirationPeriod;
        }
      }

      // For boolean flags: if the header is missing (undefined), `undefined === "true"` evaluates to false.
      // This correctly defaults the option to false if the header isn't explicitly "true".
      encryptionOption.ReadReceipt = headers["x-pgp-read-receipt"] === "true";
      encryptionOption.EncryptionConfirmation = headers["x-pgp-encryption-confirmation"] === "true";
    }
  } else {
    // If 'x-pgp-encrypt' header is not "true" (i.e., it's "false", missing, or another value),
    // then only the Encryption flag is set to false.
    // Other related properties (EncryptionMethod, ExpirationPeriod, etc.) will retain
    // their current values (which might have been set from roaming settings or defaults).
    encryptionOption.Encryption = false;
  }
}

async function InitializeTaskpaneComponents(itemID: string): Promise<EncryptionOption> {
  // Stock encryption option values
  let encryptionOption: EncryptionOption = {
    Encryption: false,
    EncryptionMethod: EncryptionType.StandardEncryption,
    SharedPassphrase: "",
    PasswordHint: "",
    ExpirationPeriod: 30,
    ReadReceipt: false,
    EncryptionConfirmation: false,
  };

  let encryptionPreferences = await getRoamingSettings();

  if (encryptionPreferences) {
    encryptionOption.EncryptionMethod = encryptionPreferences.EncryptionMethod;
    encryptionOption.ExpirationPeriod = encryptionPreferences.ExpirationPeriod;
    encryptionOption.ReadReceipt = encryptionPreferences.ReadReceipt;
    encryptionOption.EncryptionConfirmation = encryptionPreferences.EncryptionConfirmation;
  } else {
    console.log("No roaming settings found; stock values will be used.");
  }

  console.log("Fetching current item headers...");
  let headers: Record<string, string> = {};
  if (isMailboxAPI_1_8_Available) {
    headers = await GetHeaders([
      "x-pgp-encrypt",
      "x-pgp-encryption-type",
      "x-pgp-pdf-passphrase",
      "x-pgp-pdf-passphrase-hint",
      "x-pgp-expiration-period",
      "x-pgp-read-receipt",
      "x-pgp-encryption-confirmation",
    ]);
    console.log("Headers from office API: ", headers);
  } else {
    headers = await checkForPGPHeadersForComposeMail(itemID);
    console.log("Headers from EWS SOAP requests: ", headers);
  }

  await updateEncryptionOptionsFromHeaders(headers, encryptionOption);

  return encryptionOption;
}

function checkForPGPHeadersForComposeMail(itemId: string): Promise<Record<string, string>> {
  return new Promise((resolve, reject) => {
    //const headers: Record<string, string> = {}; // Initialize headers to collect values

    // EWS SOAP request to get internet headers
    const ewsRequest = `
        <soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
                       xmlns:xsd="http://www.w3.org/2001/XMLSchema"
                       xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/"
                       xmlns:t="http://schemas.microsoft.com/exchange/services/2006/types">
        <soap:Header>
          <t:RequestServerVersion Version="Exchange2013" xmlns="http://schemas.microsoft.com/exchange/services/2006/types" soap:mustUnderstand="0"/>
        </soap:Header>
        <soap:Body>
          <GetItem xmlns="http://schemas.microsoft.com/exchange/services/2006/messages">
            <ItemShape>
                <t:BaseShape>IdOnly</t:BaseShape>
                <t:AdditionalProperties>
                    <t:ExtendedFieldURI DistinguishedPropertySetId="InternetHeaders" PropertyName="x-pgp-encrypt" PropertyType="String"/>
                    <t:ExtendedFieldURI DistinguishedPropertySetId="InternetHeaders" PropertyName="x-pgp-encryption-type" PropertyType="String"/>
                    <t:ExtendedFieldURI DistinguishedPropertySetId="InternetHeaders" PropertyName="x-pgp-pdf-passphrase" PropertyType="String"/>
                    <t:ExtendedFieldURI DistinguishedPropertySetId="InternetHeaders" PropertyName="x-pgp-pdf-passphrase-hint" PropertyType="String"/>
                    <t:ExtendedFieldURI DistinguishedPropertySetId="InternetHeaders" PropertyName="x-pgp-expiration-period" PropertyType="String"/>
                    <t:ExtendedFieldURI DistinguishedPropertySetId="InternetHeaders" PropertyName="x-pgp-read-receipt" PropertyType="String"/>
                    <t:ExtendedFieldURI DistinguishedPropertySetId="InternetHeaders" PropertyName="x-pgp-encryption-confirmation" PropertyType="String"/>
                </t:AdditionalProperties>
            </ItemShape>
            <ItemIds>
              <t:ItemId Id="${itemId}" />
            </ItemIds>
          </GetItem>
        </soap:Body>
      </soap:Envelope>`;

    // Make the EWS request
    console.log("EWS Request for getting PGP headers: ", ewsRequest);
    Office.context.mailbox.makeEwsRequestAsync(ewsRequest, function (asyncResult) {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
        console.log("EWS Request Successful, result: ", asyncResult.value);
        const parsedHeaders = parseXmlAndExtractHeadersForComposeMail(asyncResult.value);
        resolve(parsedHeaders); // Resolve with parsed headers
      } else {
        console.error("Error making EWS request:", asyncResult.error.message);
        reject(asyncResult.error.message); // Reject with error
      }
    });
  });
}

// Parse the XML response to extract the relevant headers
const parseXmlAndExtractHeadersForComposeMail = (xmlBody: string): Record<string, string> => {
  console.log("Parsing XML and extracting headers");

  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlBody, "application/xml");

  // Define the namespace URIs for the query
  const ns = {
    s: "http://schemas.xmlsoap.org/soap/envelope/",
    t: "http://schemas.microsoft.com/exchange/services/2006/types",
  };

  const extendedProperties = xmlDoc.getElementsByTagNameNS(ns["t"], "ExtendedProperty");

  const headers: Record<string, string> = {};

  for (let i = 0; i < extendedProperties.length; i++) {
    const fieldUri = extendedProperties[i].getElementsByTagNameNS(ns["t"], "ExtendedFieldURI")[0];
    const propertyName = fieldUri.getAttribute("PropertyName");
    const value = extendedProperties[i].getElementsByTagNameNS(ns["t"], "Value")[0].textContent;

    if (propertyName && value) {
      headers[propertyName] = value;
    }
  }
  if (Object.keys(headers).length === 0) {
    console.log("The headers object is null or empty.");
  }
  console.log("Parsed Headers: ", headers);
  return headers; // Return extracted headers
};

function savePreferencesToRoamingSettings(
  selectedLanguage: string,
  isAutoCategorizeChecked: boolean,
  logging: string
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const settings = Office.context.roamingSettings;

    // Set each of the required settings
    console.log("Setting category: " + isAutoCategorizeChecked);
    settings.set("Language", selectedLanguage);
    settings.set("AutoCategorize", isAutoCategorizeChecked);
    settings.set("Logging", logging);

    // Save settings to persist them across sessions
    settings.saveAsync((asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
        console.log("Preferences saved to roaming settings successfully.");
        resolve(); // Resolve the promise on success
      } else {
        console.error("Failed to save preferences to roaming settings:", asyncResult.error);
        reject(asyncResult.error); // Reject the promise on failure
      }
    });
  });
}

function GetPreferencesFromRoamingSettings(): Promise<EditPreferenceOption | null> {
  return new Promise((resolve) => {
    const settings = Office.context.roamingSettings;

    // Retrieve each setting, or use default values if they do not exist
    const preferredLanguage = settings.get("Language") ?? LanguageOption.en;
    const autoCategorize = settings.get("AutoCategorize") ?? true;
    const logging = settings.get("Logging") ?? Logging.Disabled;

    console.log("Got the below roaming settings for preferences:");
    console.log("language:" + preferredLanguage);
    console.log("autoCategorize:" + autoCategorize);
    console.log("logging" + logging);
    const editPreferencesOption: EditPreferenceOption = {
      SelectedLanguage: preferredLanguage,
      categorize: autoCategorize,
      mobileNumber: true,
      Logging: logging,
    };

    // // If all settings are defaults, assume no settings were retrieved; otherwise, resolve with the encryptionOption object
    // if (preferredLanguage === LanguageOption.en && autoCategorize === true && logging === Logging.Disabled) {
    //   console.log("No preferences for roaming settings found; using default values.");
    //   resolve(null); // Indicate no settings were retrieved
    // } else {
    console.log("Sending the below roaming settings for preferences:");
    console.log("language:" + editPreferencesOption.SelectedLanguage);
    console.log("autoCategorize:" + editPreferencesOption.categorize);
    console.log("Logging" + editPreferencesOption.Logging);
    resolve(editPreferencesOption);
    // }
  });
}

async function InitializeEditPrefrenceComponents(): Promise<EditPreferenceOption> {
  const editPreferencesOption: EditPreferenceOption = {
    SelectedLanguage: LanguageOption.en,
    categorize: true,
    mobileNumber: true,
    Logging: Logging.Disabled,
  };

  let preferences = await GetPreferencesFromRoamingSettings();
  if (preferences) {
    editPreferencesOption.SelectedLanguage = preferences.SelectedLanguage;
    editPreferencesOption.categorize = preferences.categorize;
    editPreferencesOption.mobileNumber = preferences.mobileNumber;
    editPreferencesOption.Logging = preferences.Logging;
    console.log("roaming settings after found and merging");
    console.log("SelectedLanguage: " + editPreferencesOption.SelectedLanguage);
    console.log("categorize:" + editPreferencesOption.categorize);
    console.log("Logging: " + editPreferencesOption.Logging);
  } else {
    console.log("No roaming settings found; default values will be used.");
  }

  console.log("roaming settings after found and merging and returning");
  console.log("SelectedLanguage: " + editPreferencesOption.SelectedLanguage);
  console.log("categorize:" + editPreferencesOption.categorize);
  console.log("Logging: " + editPreferencesOption.Logging);
  return editPreferencesOption;
}

function InitializeUIFromOptions(encryptionOption: EncryptionOption) {
  // Initialization of encryption Toggle
  document.querySelectorAll(".ms-Toggle").forEach((toggleElement) => {
    const input = toggleElement.querySelector(".ms-Toggle-input") as HTMLInputElement;
    input.checked = encryptionOption.Encryption;
    if (typeof fabric !== "undefined" && fabric["Toggle"]) {
      new fabric["Toggle"](toggleElement);
    }
    toggleElement.querySelector(".ms-Toggle-field").classList.toggle("is-selected", input.checked);
  });

  // Encryption Method Dropdown initialization
  const dropdown = document.getElementById("encryption-select") as HTMLSelectElement;

  dropdown.value = encryptionOption.EncryptionMethod === EncryptionType.StandardEncryption ? "standard" : "shared";

  if (dropdown.value === "shared") {
    enableStandardEncryptionOptions(false);
  } else {
    enableStandardEncryptionOptions(true);
  }

  // If shared passphrase option is selected in dropdown and message is without passcode, then ask for passcode by opening passphrase dialog
  if (dropdown.value === "shared" && encryptionOption.Encryption && encryptionOption.SharedPassphrase.length === 0) {
    openPassphraseDialogBox();
  }

  // Initialize Passcode and passphrase hint if available
  if (dropdown.value === "shared" && encryptionOption.SharedPassphrase.length > 0) {
    (document.getElementById("readonly-password") as HTMLInputElement).value = encryptionOption.SharedPassphrase;
    (document.getElementById("readonly-hint") as HTMLInputElement).value = encryptionOption.PasswordHint;
    document.getElementById("form-to-show")!.style.display = "block";
  }

  // Initialize Expiration Period
  const daysInput = document.getElementById("daysInput") as HTMLInputElement;
  daysInput.value = encryptionOption.ExpirationPeriod.toString();
  const errorMsg = document.getElementById("errorMsg") as HTMLDivElement;
  if (encryptionOption.ExpirationPeriod > 30 || encryptionOption.ExpirationPeriod < 1) {
    errorMsg.style.display = "block";
  } else {
    errorMsg.style.display = "none";
  }

  // Checkboxes(Read reciepts, encryption confirmation) initialization
  const readReceiptCheckBox = document.getElementById("read-receipt") as HTMLInputElement;
  readReceiptCheckBox.checked = encryptionOption.ReadReceipt ?? false;

  const readReceiptCheckBoxLabel = document.getElementById("read-receipt-label");
  readReceiptCheckBoxLabel?.setAttribute("aria-checked", readReceiptCheckBox.checked.toString());

  const readReceiptContainer = document.getElementById("read-receipt-container");
  if (typeof fabric !== "undefined" && fabric["CheckBox"]) {
    new fabric["CheckBox"](readReceiptContainer);
  }

  const encryptionConfirmationCheckBox = document.getElementById("encryption-confirmation") as HTMLInputElement;
  encryptionConfirmationCheckBox.checked = encryptionOption.EncryptionConfirmation;

  const encryptionConfirmationCheckBoxLabel = document.getElementById("encryption-confirmation-label");
  encryptionConfirmationCheckBoxLabel?.setAttribute("aria-checked", encryptionConfirmationCheckBox.checked.toString());

  const encryptionConfirmationContainer = document.getElementById("encryption-confirmation-container");
  if (typeof fabric !== "undefined" && fabric["CheckBox"]) {
    new fabric["CheckBox"](encryptionConfirmationContainer);
  }

  DisableSaveAsDefault();
}

function InitializeEditPrefrenceFormOptions(editPreferencesOption: EditPreferenceOption) {
  console.log("Setting UI options");
  console.log("SelectedLanguage: " + editPreferencesOption.SelectedLanguage);
  console.log("categorize: " + editPreferencesOption.categorize);
  console.log("Logging: " + editPreferencesOption.Logging);
  console.log("editPreferencesOption: ", editPreferencesOption);
  // Set the language and logging dropdowns
  const languageSelect = document.getElementById("language-select") as HTMLSelectElement;
  languageSelect.value = editPreferencesOption.SelectedLanguage;
  console.log("language-select", languageSelect.value);

  const loggingSelect = document.getElementById("logging-select") as HTMLSelectElement;
  loggingSelect.value = editPreferencesOption.Logging;
  console.log("Logging Select:", loggingSelect);

  const categorizeCheckbox = document.getElementById("auto-categorize-checkbox") as HTMLInputElement;
  if (categorizeCheckbox) {
    console.log("setting categorize checkbox!");
    // Directly set to false if 'categorize' is undefined or any other condition you want to check
    categorizeCheckbox.checked = editPreferencesOption.categorize ?? false;

    const categorizeCheckboxLabel = document.getElementById("auto-categorize-checkbox-label");
    categorizeCheckboxLabel?.setAttribute("aria-checked", categorizeCheckbox.checked.toString());

    if (false == categorizeCheckbox.checked) {
      categorizeCheckboxLabel.classList.remove("is-checked");
    } else {
      categorizeCheckboxLabel.classList.add("is-checked");
    }
  }

  const mobileCheckbox = document.getElementById("remember-mobile-checkbox") as HTMLInputElement;
  if (mobileCheckbox) {
    // Directly set to false if 'mobileNumber' is undefined or any other condition you want to check
    mobileCheckbox.checked = editPreferencesOption.mobileNumber ?? false; // Directly set to false

    const mobileCheckboxLabel = document.getElementById("remember-mobile-checkbox-label");
    mobileCheckboxLabel?.setAttribute("aria-checked", mobileCheckbox.checked.toString());

    if (false == categorizeCheckbox.checked) {
      mobileCheckboxLabel.classList.remove("is-checked");
    } else {
      mobileCheckboxLabel.classList.add("is-checked");
    }
  }

  // reset update message
  const updateMessage = document.getElementById("update-message") as HTMLDivElement;
  if (updateMessage) {
    updateMessage.style.display = "none";
  }
}

async function handleUpdateButtonClick() {
  const updateMessage = document.getElementById("update-message") as HTMLDivElement;
  if (updateMessage) {
    if (isMailboxAPI_1_8_Available) {
      if (await AddCategoriesToMasterTable()) {
        updateMessage.style.display = "block";
        console.log("Master Category List has been updated.");
      }
    }
  } else {
    console.error("Update message div not found.");
  }
}

function AddEventListeners() {
  const encryptionToggle = document.getElementById("encryption-toggle") as HTMLInputElement;
  const dropdown = document.getElementById("encryption-select") as HTMLSelectElement;
  const daysInput = document.getElementById("daysInput") as HTMLInputElement;
  const optionsList = document.getElementById("optionsList") as HTMLUListElement;
  const closeButton = document.getElementById("close-dialog") as HTMLButtonElement;
  const cancelButton = document.getElementById("cancel-passphrase") as HTMLButtonElement;
  const submitButton = document.getElementById("setPassphrase") as HTMLButtonElement;
  const togglePassword = document.querySelector(".toggle-password") as HTMLImageElement;
  const togglePasswordShared = document.querySelector(".toggle-password-shared") as HTMLImageElement;
  const passwordField = document.getElementById("password-field") as HTMLInputElement;
  const hintField = document.getElementById("hint") as HTMLInputElement;
  const passwordFieldText = document.getElementById("readonly-password") as HTMLInputElement;
  const saveDefaultButton = document.querySelector("#save-as-default button") as HTMLButtonElement;
  const savePreferencesButton = document.getElementById("save-preferences");
  const updateButton = document.getElementById("update-list") as HTMLButtonElement;

  togglePasswordShared.src = "assets/eye.png";
  togglePassword.src = "assets/eye.png";

  // Event Listener for Encryption toggle
  encryptionToggle.addEventListener("change", async () => {
    const passphrase = document.getElementById("readonly-password") as HTMLInputElement;

    if (encryptionToggle.checked) {
      // Encryption toggle is turned ON
      if (dropdown.value === "shared" && passphrase.value === "") {
        openPassphraseDialogBox();
      } else {
        try {
          await AddEncryptionProperties();
          console.log("AddEncryptionHeaders completed successfully.");
        } catch (error) {
          console.error("An error occurred while adding encryption properties:", error);
        }
      }
    } else {
      // Encryption toggle is turned OFF
      try {
        document.getElementById("form-to-show").style.display = "none";
        (document.getElementById("readonly-password") as HTMLInputElement).value = "";
        (document.getElementById("readonly-hint") as HTMLInputElement).value = "";

        await RemoveEncryptionProperties();
      } catch (error) {
        console.error("An error occurred while remove encryption properties:", error);
      }
    }
  });

  // Event Listener for Encryption Method dropdown
  dropdown.addEventListener("change", () => {
    if (dropdown.value === "shared") {
      enableStandardEncryptionOptions(false);
      if (encryptionToggle.checked) {
        openPassphraseDialogBox();
      }
    } else {
      enableStandardEncryptionOptions(true);
      if (encryptionToggle.checked) {
        UpdateStandardEncryptionHeaders();
        document.getElementById("form-to-show").style.display = "none";
        (document.getElementById("readonly-password") as HTMLInputElement).value = "";
        (document.getElementById("readonly-hint") as HTMLInputElement).value = "";
      }
    }
    console.log("Change in encryption method detected, enabling save as default button.");
    EnableSaveAsDefault();
  });

  // Event Listeners for Passphrase Dialog
  closeButton.addEventListener("click", () => closeDialog(false));
  cancelButton.addEventListener("click", () => closeDialog(false));
  // Submit button click event
  submitButton.addEventListener("click", () => {
    const passphrase = passwordField.value.trim();
    const hint = hintField.value.trim();

    // Only proceed if passphrase is valid
    if (passphrase.length < 6 || passphrase === hint) {
      return; // Prevent further actions if validation fails
    }

    // Passphrase is valid, proceed with the logic
    console.log("Passphrase submitted:", passphrase, "Hint:", hint);
    (document.getElementById("readonly-password") as HTMLInputElement).value = passphrase;
    (document.getElementById("readonly-hint") as HTMLInputElement).value = hint;

    // Close the dialog and update headers
    closeDialog(true);
    UpdateSharePassphraseEncryptionHeaders(passphrase, hint);
    document.getElementById("form-to-show")!.style.display = "block"; // Show the form if necessary
    // EnableSaveAsDefault();
  });

  // Initial validation check in case the user enters values before the page loads
  validatePassword();

  // Regex pattern for password validation (1 uppercase, 1 lowercase, 1 number, 1 symbol, minimum 6 characters)
  // const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{6,}$/;

  // Function to check if the form is valid
  function validatePassword() {
    const passphrase = passwordField.value.trim();
    const hint = hintField.value.trim();
    const isValid = passphrase.length >= 6 && passphrase !== hint;

    passwordField.classList.toggle("valid", isValid);
    passwordField.classList.toggle("invalid", !isValid);

    submitButton.disabled = !isValid;
  }

  function validatePasswordInput() {
    validatePassword();

    const passphrase = passwordField.value.trim();
    const hint = hintField.value.trim();
    const isValidPassword = passphrase.length >= 6 && passphrase !== hint;
    const isValidHint = hint.length >= 0 && passphrase !== hint;

    passwordField.style.borderColor = isValidPassword ? "#E0E0E0" : "red";

    if (isValidHint) {
      hintField.style.borderColor = "#E0E0E0";
    }
  }

  function validatePasswordHint() {
    validatePassword();

    const passphrase = passwordField.value.trim();
    const hint = hintField.value.trim();
    const isValidPassword = passphrase.length >= 6 && passphrase !== hint;
    const isValidHint = hint.length >= 0 && passphrase !== hint;

    hintField.style.borderColor = isValidHint ? "#E0E0E0" : "red";

    if (isValidPassword) {
      passwordField.style.borderColor = "#E0E0E0";
    }
  }

  // Event listener to check for changes in the fields
  passwordField.addEventListener("input", () => validatePasswordInput());
  hintField.addEventListener("input", () => validatePasswordHint());

  // Event Listeners for read receipt and encryption confirmation checkboxes
  const checkBoxIds = ["read-receipt", "encryption-confirmation"]; // Add more checkbox ids here if needed

  checkBoxIds.forEach((checkboxId, index) => {
    const checkboxInput = document.getElementById(checkboxId) as HTMLInputElement;
    const isReadReceipt = index === 0;

    checkboxInput?.addEventListener("change", () => {
      const isChecked = checkboxInput.checked;

      if (isReadReceipt) {
        updateReadReceiptHeader(isChecked);
      } else {
        updateEncryptionConfirmationHeader(isChecked);
      }

      console.log("Enabling because of checkboxes");
      EnableSaveAsDefault();
    });
  });

  // Expiration Days Input
  daysInput.addEventListener("click", () => {
    optionsList.style.display = optionsList.style.display === "none" ? "block" : "none";
  });

  // Save as Default button click event
  saveDefaultButton.addEventListener("click", () => {
    SaveUserSettings();
  });
  // Hide options list when clicking outside
  document.addEventListener("click", function (event) {
    const target = event.target as Node;
    if (!daysInput.contains(target) && !optionsList.contains(target)) {
      optionsList.style.display = "none";
    }
  });
  optionsList.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    if (target.tagName === "LI") {
      const selectedValue = target.getAttribute("data-value"); // Get the data-value of the selected li
      if (selectedValue) {
        // Set the selected value to the daysInput field
        daysInput.value = selectedValue;

        // Call validateInput to handle any validation logic
        validateInput(parseInt(selectedValue, 10));

        // Hide the options list after selection
        optionsList.style.display = "none";
      }
    }
  });

  // Validate input
  daysInput.addEventListener("input", () => {
    const value = parseInt(daysInput.value, 10);
    optionsList.style.display = "none";
    validateInput(value);
  });

  // Show password button functionality
  if (togglePassword && passwordFieldText) {
    passwordFieldText.style.paddingRight = "31px";

    togglePassword.src = "assets/eye.png";
    // Initial state of the icon
    togglePassword.src = passwordFieldText.type === "password" ? "assets/eye.png" : "assets/eye-off.png";

    togglePassword.addEventListener("click", () => {
      // Toggle the input type between password and text
      if (passwordFieldText.type === "password") {
        passwordFieldText.type = "text";
        togglePassword.src = "assets/eye-off.png"; // Show eye-off icon
      } else {
        passwordFieldText.type = "password";
        togglePassword.src = "assets/eye.png"; // Show eye icon
      }
    });
  }
  if (togglePasswordShared && passwordField) {
    passwordField.style.paddingRight = "31px";

    togglePasswordShared.src = "assets/eye.png";
    // Initial state of the icon
    togglePasswordShared.src = passwordField.type === "password" ? "assets/eye.png" : "assets/eye-off.png";

    togglePasswordShared.addEventListener("click", () => {
      // Toggle the input type between password and text
      if (passwordField.type === "password") {
        passwordField.type = "text";
        togglePasswordShared.src = "assets/eye-off.png"; // Show eye-off icon
      } else {
        passwordField.type = "password";
        togglePasswordShared.src = "assets/eye.png"; // Show eye icon
      }
    });
  }

  const autoCategorizeContainer = document.getElementById("auto-categorize-checkbox-container");
  if (autoCategorizeContainer) {
    if (typeof fabric !== "undefined" && fabric["CheckBox"]) {
      new fabric["CheckBox"](autoCategorizeContainer);
    }
  }

  const rememberMobileContainer = document.getElementById("remember-mobile-checkbox-container");
  if (rememberMobileContainer) {
    if (typeof fabric !== "undefined" && fabric["CheckBox"]) {
      new fabric["CheckBox"](rememberMobileContainer);
    }
  }

  // Event listener for open Edit Preference Dialog
  const editPreferencesContainer = document.querySelector(".close-edit-preferences");
  const editPreferencesButton = editPreferencesContainer?.querySelector(".open-edit-preference") as HTMLButtonElement;
  const editPreferencesDialog = editPreferencesContainer?.querySelector(".ms-Dialog");
  if (editPreferencesButton && editPreferencesDialog) {
    const dialogComponent = new fabric["Dialog"](editPreferencesDialog);
    editPreferencesButton.onclick = () => openDialog(dialogComponent);
  }

  async function openDialog(dialogComponent) {
    const editPrefrenceOption = await InitializeEditPrefrenceComponents();
    console.log("Edit Preferences used to initialize UI: ", JSON.stringify(editPrefrenceOption));
    InitializeEditPrefrenceFormOptions(editPrefrenceOption);
    dialogComponent.open();
  }

  // Change More options icon from not expanded to expanded stae and vice versa
  document.getElementById("toggleArea")?.addEventListener("click", function () {
    let panel = document.getElementById("panel");
    let button: HTMLImageElement | null = document.getElementById("toggleButton") as HTMLImageElement;

    if (panel) {
      // Toggle the display of the panel
      if (panel.style.display === "none" || panel.style.display === "") {
        panel.style.display = "block";
        if (button) {
          button.src = "assets/down-arrow.png";
        }
      } else {
        panel.style.display = "none";
        if (button) {
          button.src = "assets/left-arrow.png";
        }
      }
    }
  });

  // Event listener for Save Preferences button
  if (savePreferencesButton) {
    savePreferencesButton.addEventListener("click", handleSavePreferences);
  } else {
    console.error("Save Preferences button not found.");
  }
  //
  if (updateButton) {
    updateButton.addEventListener("click", handleUpdateButtonClick);
    console.log("Edit button listener added.");
  } else {
    console.error("Edit button not found.");
  }
}

function saveRoamingSettings(
  encryptionMethod: EncryptionType,
  expirationPeriod: number,
  readReceipt: boolean,
  encryptionConfirmation: boolean
) {
  const settings = Office.context.roamingSettings;

  // Set each of the required settings
  settings.set("EncryptionMethod", encryptionMethod);
  settings.set("ExpirationPeriod", expirationPeriod);
  settings.set("ReadReceipt", readReceipt);
  settings.set("EncryptionConfirmation", encryptionConfirmation);

  // Save settings to persist them across sessions
  settings.saveAsync((asyncResult) => {
    if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
      console.log("Roaming settings saved successfully.");
    } else {
      console.error("Failed to save roaming settings:", asyncResult.error);
    }
  });
}

function getRoamingSettings(): Promise<EncryptionOption | null> {
  return new Promise((resolve) => {
    const settings = Office.context.roamingSettings;

    // Retrieve each setting, or use default values if they do not exist
    const encryptionMethod = settings.get("EncryptionMethod") ?? EncryptionType.StandardEncryption;
    const expirationPeriod = settings.get("ExpirationPeriod") ?? 30;
    const readReceipt = settings.get("ReadReceipt") ?? false;
    const encryptionConfirmation = settings.get("EncryptionConfirmation") ?? false;

    // Construct the EncryptionOption object based on retrieved or default values
    const encryptionOption: EncryptionOption = {
      Encryption: false, // default as no information from roaming settings
      EncryptionMethod: encryptionMethod,
      SharedPassphrase: "", // default as no information from roaming settings
      PasswordHint: "", // default as no information from roaming settings
      ExpirationPeriod: expirationPeriod,
      ReadReceipt: readReceipt,
      EncryptionConfirmation: encryptionConfirmation,
    };

    // If all settings are defaults, assume no settings were retrieved; otherwise, resolve with the encryptionOption object
    if (
      encryptionMethod === EncryptionType.StandardEncryption &&
      expirationPeriod === 30 &&
      readReceipt === false &&
      encryptionConfirmation === false
    ) {
      console.log("No roaming settings found; using default values.");
      resolve(null); // Indicate no settings were retrieved
    } else {
      resolve(encryptionOption);
    }
  });
}

function SaveUserSettings() {
  console.log("Saving User Settings...");
  // Get All values from UI here
  const dropdown = document.getElementById("encryption-select") as HTMLSelectElement;
  const expirationPeriod = parseInt((document.getElementById("daysInput") as HTMLInputElement).value, 10);
  const readReceipt = document.getElementById("read-receipt") as HTMLInputElement;
  const encryptionConfirmation = document.getElementById("encryption-confirmation") as HTMLInputElement;

  let EncryptionMethod = EncryptionType.StandardEncryption;
  if (dropdown.value === "shared") {
    EncryptionMethod = EncryptionType.SharedPassphraseEncryption;
  } else {
    EncryptionMethod = EncryptionType.StandardEncryption;
  }

  saveRoamingSettings(
    EncryptionMethod,
    expirationPeriod,
    readReceipt.checked ? true : false,
    encryptionConfirmation.checked ? true : false
  );

  DisableSaveAsDefault(); // Disable after saving
}

// Enable Save as Default button and reset text
function EnableSaveAsDefault() {
  const saveDefaultButton = document.querySelector("#save-as-default button") as HTMLButtonElement;
  const saveLabel = document.getElementById("save-as-deafault-label") as HTMLSpanElement;
  const saveDefaultDiv = document.getElementById("save-as-default");

  if (saveDefaultButton && document.getElementById("errorMsg").style.display === "none") {
    console.log("Enabling Save as Default");
    saveDefaultButton.disabled = false; // Enable the button
    if (saveLabel) {
      savedLabel = false;
      saveLabel.textContent = defaultLanguage.saveAsDefault;
    }
    if (saveDefaultDiv) {
      saveDefaultDiv.style.display = "block"; // Show the button container
    }
  } else {
    console.error("Save as Default button not found.");
  }
}

// Disable Save as Default button and change text
function DisableSaveAsDefault() {
  const saveDefaultButton = document.querySelector("#save-as-default button") as HTMLButtonElement;
  const saveLabel = document.getElementById("save-as-deafault-label") as HTMLSpanElement;

  if (saveDefaultButton) {
    console.log("Disabling Save as Default");
    saveDefaultButton.disabled = true; // Disable the button
    if (saveLabel) {
      savedLabel = true;
      saveLabel.textContent = defaultLanguage.saved; // Set the label to "Saved" in the current language
    }
  }
}

// Function to clear the passphrase form fields
function ClearPassphraseForm() {
  const passwordField = document.getElementById("password-field") as HTMLInputElement;
  const hintField = document.getElementById("hint") as HTMLInputElement;
  const passwordErrorDiv = document.getElementById("password-error") as HTMLDivElement;
  const passwordSuccessDiv = document.getElementById("password-success") as HTMLDivElement;
  const submitButton = document.getElementById("setPassphrase") as HTMLButtonElement;

  // Clear the fields and hide messages
  if (passwordField && hintField) {
    passwordField.value = "";
    hintField.value = "";
    passwordField.style.borderColor = ""; // Reset border color
    hintField.style.borderColor = ""; // Reset border color
  }

  if (passwordErrorDiv) passwordErrorDiv.style.display = "none";
  if (passwordSuccessDiv) passwordSuccessDiv.style.display = "none";

  if (submitButton) submitButton.disabled = true;

  console.log("Passphrase form cleared successfully.");
}

async function AddEncryptionProperties() {
  console.log("Adding encryption headers...");
  const dropdown = document.getElementById("encryption-select") as HTMLSelectElement;
  const passphrase = document.getElementById("readonly-password") as HTMLInputElement;
  const hint = document.getElementById("readonly-hint") as HTMLInputElement;
  let expirationPeriod = parseInt((document.getElementById("daysInput") as HTMLInputElement).value, 10);
  const readReceipt = document.getElementById("read-receipt") as HTMLInputElement;
  const encryptionConfirmation = document.getElementById("encryption-confirmation") as HTMLInputElement;

  let headers: { [key: string]: string } = {};
  let categories: string[] = [];
  let message = "Email is being encrypted";
  // Determine headers and categories based on dropdown
  if (dropdown.value === "shared") {
    headers = {
      "x-pgp-encrypt": "true",
      "x-pgp-encryption-type": "pdf",
      "x-pgp-pdf-passphrase": passphrase.value,
      "x-pgp-pdf-passphrase-hint": hint.value,
    };

    categories = ["Shared Key Encryption"];
    message = defaultLanguage.sharedPassphraseEncryptionNotification;
  } else {
    if (isNaN(expirationPeriod) || expirationPeriod < 1 || expirationPeriod > 30) {
      expirationPeriod = Office.context.roamingSettings.get("ExpirationPeriod") ?? 30;
    }
    headers = {
      "x-pgp-encrypt": "true",
      "x-pgp-encryption-type": "standard",
    };

    if (addinMode === AddInMode.Advanced || addinMode === AddInMode.OnPrem) {
      if (readReceipt.checked) {
        headers["x-pgp-read-receipt"] = "true";
      }
    }

    if (addinMode === AddInMode.Advanced) {
      headers["x-pgp-expiration-period"] = expirationPeriod.toString();
    }

    categories = ["Standard Encryption"];
    message = defaultLanguage.standardEncryptionNotification;
  }

  if (addinMode === AddInMode.Advanced || addinMode === AddInMode.OnPrem) {
    if (encryptionConfirmation.checked) {
      headers["x-pgp-encryption-confirmation"] = "true";
    }
  }

  try {
    const autoCategorize = Office.context.roamingSettings.get("AutoCategorize") ?? true;

    if (isMailboxAPI_1_8_Available) {
      await AddHeaders(headers);

      await AddCategoriesToMasterTable();
      if (
        isM365Exchange &&
        (clientHost == OutlookClientHost.NewOutlookWindows || clientHost == OutlookClientHost.OutlookWeb)
      ) {
        console.log("Office 365 env, categorization with graph API");
        if (categories[0] === "Standard Encryption") {
          await deleteMessageCategoriesViaGraph(accessToken, graphRestId, ["Shared Key Encryption"]);
        } else if (categories[0] === "Shared Key Encryption") {
          await deleteMessageCategoriesViaGraph(accessToken, graphRestId, ["Standard Encryption"]);
        }
        if (autoCategorize) {
          await appendMessageCategoriesViaGraph(accessToken, graphRestId, categories);
        }
      } else {
        // Check the value of categories[0] and remove the other category
        if (categories[0] === "Standard Encryption") {
          await removeCategoryFromItem("Shared Key Encryption");
        } else if (categories[0] === "Shared Key Encryption") {
          await removeCategoryFromItem("Standard Encryption");
        }
        if (autoCategorize) {
          await addCategoryToItem(categories[0]);
        }
      }
    } else {
      // updates headers and sets category
      await updateItemProperties(headers, [], autoCategorize ? categories : [], itemID);
      console.log("updateItemProperties completed.");
    }
    var operations = await generateCustomPropertyOperations([], headers, []);
    console.log("Adding Custom Properties: " + JSON.stringify(operations, null, 2));
    await batchCustomProperties(operations);
    removeNotification();
    showNotification(message);
  } catch (error) {
    console.error("Error while updating item properties:", error);
  }
}

async function RemoveEncryptionProperties() {
  try {
    const headers = [
      "x-pgp-encrypt",
      "x-pgp-encryption-type",
      "x-pgp-pdf-passphrase",
      "x-pgp-pdf-passphrase-hint",
      "x-pgp-expiration-period",
      "x-pgp-read-receipt",
      "x-pgp-encryption-confirmation",
    ];

    if (isMailboxAPI_1_8_Available) {
      await RemoveHeaders(headers);

      await removeCategoryFromItem("Standard Encryption");
      await removeCategoryFromItem("Shared Key Encryption");
    } else {
      await RemoveItemProperties(headers, true, itemID);
    }
    var operations = await generateCustomPropertyOperations([], {}, headers);
    console.log("Removing Custom Properties: " + JSON.stringify(operations, null, 2));
    await batchCustomProperties(operations);

    console.log("All operations completed successfully.");
    removeNotification();
  } catch (error) {
    console.error("Error while removing encryption properties:", error);
  }
}

async function UpdateStandardEncryptionHeaders() {
  console.log("Updating standard encryption headers...");

  const encryptionToggle = document.getElementById("encryption-toggle") as HTMLInputElement;

  if (encryptionToggle.checked) {
    let expirationPeriod = parseInt((document.getElementById("daysInput") as HTMLInputElement).value, 10);
    const readReceipt = document.getElementById("read-receipt") as HTMLInputElement;
    const encryptionConfirmation = document.getElementById("encryption-confirmation") as HTMLInputElement;

    if (expirationPeriod < 1 || expirationPeriod > 30) {
      expirationPeriod = Office.context.roamingSettings.get("ExpirationPeriod") ?? 30;
    }

    const headers = {
      "x-pgp-encrypt": "true",
      "x-pgp-encryption-type": "standard",
    };

    if (addinMode === AddInMode.Advanced || addinMode === AddInMode.OnPrem) {
      if (readReceipt.checked) {
        headers["x-pgp-read-receipt"] = "true";
      }

      if (encryptionConfirmation.checked) {
        headers["x-pgp-encryption-confirmation"] = "true";
      }
    }

    if (addinMode === AddInMode.Advanced) {
      headers["x-pgp-expiration-period"] = expirationPeriod.toString();
    }

    const autoCategorize = Office.context.roamingSettings.get("AutoCategorize") ?? true;

    if (isMailboxAPI_1_8_Available) {
      await AddHeaders(headers);
      await RemoveHeaders(["x-pgp-pdf-passphrase", "x-pgp-pdf-passphrase-hint"]);

      console.log("Adding Categories to master table");
      await AddCategoriesToMasterTable();
      if (
        isM365Exchange &&
        (clientHost == OutlookClientHost.NewOutlookWindows || clientHost == OutlookClientHost.OutlookWeb)
      ) {
        console.log("Office 365 env, categorization with graph API");
        await deleteMessageCategoriesViaGraph(accessToken, graphRestId, ["Shared Key Encryption"]);
        if (autoCategorize) {
          await appendMessageCategoriesViaGraph(accessToken, graphRestId, ["Standard Encryption"]);
        }
      } else {
        await removeCategoryFromItem("Shared Key Encryption");

        if (autoCategorize) {
          console.log("Adding Category");
          await addCategoryToItem("Standard Encryption");
        }
      }
    } else {
      if (autoCategorize) {
        await updateItemProperties(
          headers,
          ["x-pgp-pdf-passphrase", "x-pgp-pdf-passphrase-hint"],
          ["Standard Encryption"],
          itemID
        );
      } else {
        RemoveItemProperties([], true, itemID);
        await updateItemProperties(headers, ["x-pgp-pdf-passphrase", "x-pgp-pdf-passphrase-hint"], [], itemID);
      }
    }

    var operations = await generateCustomPropertyOperations([], headers, [
      "x-pgp-pdf-passphrase",
      "x-pgp-pdf-passphrase-hint",
    ]);
    console.log("Updating Custom Properties (Standard Encryption): " + JSON.stringify(operations, null, 2));
    await batchCustomProperties(operations);

    removeNotification();
    showNotification(defaultLanguage.standardEncryptionNotification);
  }
}

async function UpdateSharePassphraseEncryptionHeaders(passphrase: string, hint: string) {
  console.log("Updating shared passphrase encryption headers...");

  const encryptionToggle = document.getElementById("encryption-toggle") as HTMLInputElement;

  if (encryptionToggle.checked) {
    const headers = {
      "x-pgp-encrypt": "true",
      "x-pgp-encryption-type": "pdf",
      "x-pgp-pdf-passphrase": passphrase,
      "x-pgp-pdf-passphrase-hint": hint,
    };

    if (addinMode == AddInMode.Advanced || addinMode === AddInMode.OnPrem) {
      const encryptionConfirmation = document.getElementById("encryption-confirmation") as HTMLInputElement;
      if (encryptionConfirmation.checked) {
        headers["x-pgp-encryption-confirmation"] = "true";
      }
    }

    const autoCategorize = Office.context.roamingSettings.get("AutoCategorize") ?? true;

    if (isMailboxAPI_1_8_Available) {
      await AddHeaders(headers);
      await RemoveHeaders(["x-pgp-expiration-period", "x-pgp-read-receipt"]);

      console.log("Adding Categories");
      await AddCategoriesToMasterTable();
      if (
        isM365Exchange &&
        (clientHost == OutlookClientHost.NewOutlookWindows || clientHost == OutlookClientHost.OutlookWeb)
      ) {
        console.log("Office 365 env, categorization with graph API");
        await deleteMessageCategoriesViaGraph(accessToken, graphRestId, ["Standard Encryption"]);
        if (autoCategorize) {
          await appendMessageCategoriesViaGraph(accessToken, graphRestId, ["Shared Key Encryption"]);
        }
      } else {
        await removeCategoryFromItem("Standard Encryption");
        if (autoCategorize) {
          await addCategoryToItem("Shared Key Encryption");
        }
      }
    } else {
      if (autoCategorize) {
        await updateItemProperties(
          headers,
          ["x-pgp-expiration-period", "x-pgp-read-receipt"],
          ["Shared Key Encryption"],
          itemID
        );
      } else {
        await RemoveItemProperties([], true, itemID);
        await updateItemProperties(headers, ["x-pgp-expiration-period", "x-pgp-read-receipt"], [], itemID);
      }
    }

    var operations = await generateCustomPropertyOperations([], headers, [
      "x-pgp-expiration-period",
      "x-pgp-read-receipt",
    ]);
    console.log("Updating Custom Properties (Shared Passphrase): " + JSON.stringify(operations, null, 2));
    await batchCustomProperties(operations);

    removeNotification();
    showNotification(defaultLanguage.sharedPassphraseEncryptionNotification);
  }
}

async function updateExpirationHeader(expirationPeriod: number) {
  if (addinMode === AddInMode.Advanced) {
    console.log("Updating expiration header...");
    const encryptionToggle = document.getElementById("encryption-toggle") as HTMLInputElement;
    if (encryptionToggle.checked) {
      if (expirationPeriod < 1 || expirationPeriod > 30) {
        expirationPeriod = Office.context.roamingSettings.get("ExpirationPeriod") ?? 30;
      }
      const header = {
        "x-pgp-expiration-period": expirationPeriod.toString(),
      };
      if (isMailboxAPI_1_8_Available) {
        await AddHeaders(header);
      } else {
        await updateItemProperties(header, [], [], itemID);
      }
      var operations = await generateCustomPropertyOperations([], header, []);
      console.log("Updating Custom Properties (Expiration Header): " + JSON.stringify(operations, null, 2));
      await batchCustomProperties(operations);
    }
  }
}

async function updateReadReceiptHeader(isEnabled: boolean) {
  if (addinMode === AddInMode.Advanced || addinMode === AddInMode.OnPrem) {
    console.log("Updating read receipt headers:", isEnabled);
    const encryptionToggle = document.getElementById("encryption-toggle") as HTMLInputElement;

    if (encryptionToggle.checked) {
      if (isEnabled) {
        const header = {
          "x-pgp-read-receipt": isEnabled.toString(), // Convert to string for header
        };
        if (isMailboxAPI_1_8_Available) {
          await AddHeaders(header);
        } else {
          await updateItemProperties(header, [], [], itemID);
        }
        var operations = await generateCustomPropertyOperations([], header, []);
        console.log("Updating Custom properties (Read Receipt Enabled): " + JSON.stringify(operations, null, 2));
        await batchCustomProperties(operations);
      } else {
        if (isMailboxAPI_1_8_Available) {
          await RemoveHeaders(["x-pgp-read-receipt"]);
        } else {
          await RemoveItemProperties(["x-pgp-read-receipt"], false, itemID);
        }
        var operationsToRemove = await generateCustomPropertyOperations([], {}, ["x-pgp-read-receipt"]);
        console.log(
          "Updating custom properties (Read Receipt Disabled): " + JSON.stringify(operationsToRemove, null, 2)
        );
        await batchCustomProperties(operationsToRemove);
      }
    }
  }
}

async function updateEncryptionConfirmationHeader(isEnabled: boolean) {
  if (addinMode === AddInMode.Advanced || addinMode === AddInMode.OnPrem) {
    console.log("Updating encryption confirmation header:", isEnabled);
    const encryptionToggle = document.getElementById("encryption-toggle") as HTMLInputElement;

    if (encryptionToggle.checked) {
      if (isEnabled) {
        const headers = {
          "x-pgp-encryption-confirmation": isEnabled.toString(), // Convert to string for header
        };
        if (isMailboxAPI_1_8_Available) {
          await AddHeaders(headers);
        } else {
          await updateItemProperties(headers, [], [], itemID);
        }
        var operations = await generateCustomPropertyOperations([], headers, []);
        console.log(
          "Updating custom properties (Encryption Confirmation Enabled): " + JSON.stringify(operations, null, 2)
        );
        await batchCustomProperties(operations);
      } else {
        if (isMailboxAPI_1_8_Available) {
          await RemoveHeaders(["x-pgp-encryption-confirmation"]);
        } else {
          await RemoveItemProperties(["x-pgp-encryption-confirmation"], false, itemID);
        }
        var operationsToRemove = await generateCustomPropertyOperations([], {}, ["x-pgp-encryption-confirmation"]);
        console.log(
          "Updating Custom properties (Encryption Confirmation Disabled): " +
            JSON.stringify(operationsToRemove, null, 2)
        );
        await batchCustomProperties(operationsToRemove);
      }
    }
  }
}

function validateInput(value: number) {
  const errorMsg = document.getElementById("errorMsg") as HTMLDivElement;
  if (isNaN(value) || value > 30 || value < 1) {
    errorMsg.style.display = "block";
    document.getElementById("save-as-default").style.display = "none";
    const expirationPeriod = Office.context.roamingSettings.get("ExpirationPeriod") ?? 30;
    updateExpirationHeader(expirationPeriod);
  } else {
    errorMsg.style.display = "none";
    updateExpirationHeader(value);
    // Enable Save as default button
    console.log("Enabling validate input");
    EnableSaveAsDefault();
  }
}

function openPassphraseDialogBox() {
  ClearPassphraseForm();
  const dialogBox = document.getElementById("passphrase-dialog") as HTMLElement;
  const dialogOverlay = document.getElementById("dialog-overlay") as HTMLElement;

  if (dialogBox && dialogOverlay) {
    dialogBox.style.display = "block";
    dialogOverlay.style.display = "block";
  } else {
    console.error("Dialog box or overlay not found.");
  }
}

function closeDialog(isSubmit: boolean) {
  if (!isSubmit) {
    RemoveEncryptionProperties();
    document.querySelectorAll(".ms-Toggle").forEach((toggleElement) => {
      const input = toggleElement.querySelector(".ms-Toggle-input") as HTMLInputElement;
      input.checked = false;
      toggleElement.querySelector(".ms-Toggle-field").classList.toggle("is-selected", input.checked);
    });
  }

  const dialogBox = document.getElementById("passphrase-dialog") as HTMLElement;
  const dialogOverlay = document.getElementById("dialog-overlay") as HTMLElement;

  if (dialogBox && dialogOverlay) {
    dialogBox.style.display = "none";
    dialogOverlay.style.display = "none";
  }
}

// toggle Remember Mobile Checkbox
function toggleRememberMobileCheckbox() {
  const checkboxContainer = document.getElementById("remember-mobile-checkbox-container");

  if (checkboxContainer) {
    if (showMobileNumberCheckbox) {
      checkboxContainer.style.display = "block"; // Show the checkbox
    } else {
      checkboxContainer.style.display = "none"; // Hide the checkbox
    }
  } else {
    console.error("Checkbox container not found.");
  }
}

// Mobile Number hide and show
function setMobileNumberCheckboxVisibility(shouldShow: boolean) {
  showMobileNumberCheckbox = shouldShow;
  toggleRememberMobileCheckbox();
}

/**
 * Enables or disables UI options based on the selected encryption type.
 * - Expiration period is only shown for Standard Encryption in Advanced mode.
 * - Read receipt is only shown for Standard Encryption.
 * @param isStandard - True if Standard Encryption is selected, false otherwise.
 */
function enableStandardEncryptionOptions(isStandard: boolean) {
  const expirationContainer = document.querySelector(".pgp-dropdown-days");
  const readReceiptContainer = document.getElementById("read-receipt-container");

  if (expirationContainer) {
    // Expiration options are only available in Advanced mode for Standard Encryption.
    const showExpiration = isStandard && addinMode === AddInMode.Advanced;
    (expirationContainer as HTMLElement).style.display = showExpiration ? "flex" : "none";
  }
  if (readReceiptContainer) {
    readReceiptContainer.style.display = isStandard ? "block" : "none";
  }
}

const updateTextContent = (elementId: string, content: string | null, isTitle = false) => {
  const element = document.getElementById(elementId);

  if (element) {
    // If it's a form element (input/textarea) or it's meant to update the title
    if (isTitle) {
      // Update the title (tooltip) attribute if it's for a tooltip
      element.setAttribute("title", content || "");
    } else if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
      // Update placeholder if it's a form element
      element.placeholder = content || "";
    } else {
      // Update textContent for other elements
      element.textContent = content || "";
    }
  } else {
    console.warn(`Element with id '${elementId}' not found.`);
  }
};

// Load localization texts based on selected language
function loadLocalization() {
  const localization = defaultLanguage;
  console.log("localization=======", localization);
  const loggingSelect = document.getElementById("logging-select") as HTMLSelectElement;
  const logging = loggingSelect ? loggingSelect.value : null;
  if (localization) {
    updateTextContent("encrypt-message", localization.encryptMessage);
    updateTextContent("encryption-method", localization.encryptionMethod);
    updateTextContent("encryption-select-standard", localization.encryptionSelectStandard);
    updateTextContent("encryption-select-shared", localization.encryptionSelectShared);
    updateTextContent("passcode", localization.passcode);
    updateTextContent("passcode-label", localization.passcodeLabel);
    updateTextContent("passcode-hint-label", localization.passcodeHint);
    updateTextContent("passcode-hint-label-text", localization.passcodeHint);
    updateTextContent("password-field", localization.placeholderPasscode);
    updateTextContent("hint", localization.placeholderHint);
    updateTextContent("shared-passphrase", localization.sharedPassphrase);
    updateTextContent("enter-shared-passphrase", localization.enterSharedPassphrase);
    updateTextContent("set-passphrase", localization.setPassphrase);
    updateTextContent("cancel", localization.cancel);
    updateTextContent("more-options", localization.moreOptions);
    updateTextContent("expiration-period", localization.expirationPeriod);
    updateTextContent("days", localization.days);
    updateTextContent("errorMsg", localization.errorMsg);
    updateTextContent("send-receipt", localization.sendReceipt);
    updateTextContent("confirm-encryption", localization.confirmEncryption);
    if (savedLabel) {
      updateTextContent("save-as-deafault-label", localization.saved);
    } else {
      updateTextContent("save-as-deafault-label", localization.saveAsDefault);
    }
    updateTextContent("preferences", localization.preferences);
    updateTextContent("language-label", localization.language);
    updateTextContent("categorization", localization.categorization);
    updateTextContent("auto-categorize", localization.autoCategorize);
    updateTextContent("save-preferences-cancel", localization.savePreferencesCancel);
    updateTextContent("update-category-list", localization.updateCategoryList);
    updateTextContent("remember-mobile", localization.rememberMobile);
    updateTextContent("logging-label", localization.logging);
    updateTextContent("save-preferences", localization.savePreferences);
    updateTextContent("edit-preferences", localization.editPreferences);
    updateTextContent("footer-copyright", localization.footerCopyright);
    updateTextContent("footer-version", localization.footerVersion);
    updateTextContent("update-message", localization.updateCategoryMessage);
    updateTextContent("info-icon-passcode", localization.tooltip["info-icon-passcode"], true);
    updateTextContent("info-icon-hint", localization.tooltip["info-icon-hint"], true);
    updateTextContent("shared-info-icon-hint", localization.tooltip["info-icon-hint"], true);
    updateTextContent("info-icon-more-options", localization.tooltip["info-icon-more-options"], true);
    updateTextContent("info-icon-categorization", localization.tooltip["info-icon-categorization"], true);
    updateTextContent("update-list", localization.tooltip["info-update-category-list"], true);

    // Populate language options
    populateLanguageOption(localization.languageOptions);

    // Populate logging options
    populateLoggingOption(localization.loggingOptions, logging);
  } else {
    console.error("localization is undefined or not loaded");
  }
}

// Function to populate the Language dropdown
function populateLanguageOption(options: { [key: string]: string }) {
  const languageSelect = document.getElementById("language-select") as HTMLSelectElement;

  // Clear any existing options
  languageSelect.innerHTML = "";
  Object.keys(options).forEach((languageCode) => {
    const optionElement = document.createElement("option");
    optionElement.value = languageCode;
    optionElement.textContent = options[languageCode]; // The language name
    languageSelect.appendChild(optionElement);
  });
}

// Function to populate the Logging dropdown
function populateLoggingOption(loggingOptions: { [key: string]: string }, selectedLogging: string) {
  const loggingSelect = document.getElementById("logging-select") as HTMLSelectElement;

  // Clear existing options
  loggingSelect.innerHTML = "";

  // Create new options based on the provided object (loggingOptions)
  Object.keys(loggingOptions).forEach((optionKey) => {
    const optionElement = document.createElement("option");
    optionElement.value = optionKey; // Option key (e.g., 'Error', 'Warning')
    optionElement.textContent = loggingOptions[optionKey]; // Option label (e.g., 'Error', 'Warning')
    loggingSelect.appendChild(optionElement);
  });

  // Set the default value (selected logging option)
  if (loggingOptions[selectedLogging]) {
    loggingSelect.value = selectedLogging;
  } else {
    loggingSelect.value = Object.keys(loggingOptions)[0]; // Fallback to first option if invalid value
  }

  console.log("Default selected value:", loggingSelect.value); // Debugging log
}

// Handle the "Save Preferences" button click
async function handleSavePreferences(event: Event) {
  event.preventDefault();
  const languagedropdown = document.getElementById("language-select") as HTMLSelectElement;
  const selectedLanguage = languagedropdown ? languagedropdown.value : "en";
  // Setting the language and loading it on UI
  defaultLanguage = language[selectedLanguage];
  loadLocalization();
  const autoCategorizeCheckbox = document.getElementById("auto-categorize-checkbox") as HTMLInputElement;
  const isAutoCategorizeChecked = autoCategorizeCheckbox.checked;

  const mobileNumberCheckbox = document.getElementById("remember-mobile-checkbox") as HTMLInputElement;
  const isMobileNumberChecked = mobileNumberCheckbox.checked;

  const loggingSelect = document.getElementById("logging-select") as HTMLSelectElement;
  const logging = loggingSelect ? loggingSelect.value : null;

  // Log the values to the console (replace this with your actual save logic)
  console.log("Language:", selectedLanguage);
  console.log("Automatically categorize messages:", isAutoCategorizeChecked);
  console.log("Remember mobile numbers:", isMobileNumberChecked);
  console.log("Logging setting:", logging);
  await savePreferencesToRoamingSettings(selectedLanguage, isAutoCategorizeChecked, logging);
}

function setLoggingLevelVisibility(showLoggingDropdown: boolean) {
  const loggingDropdownContainer = document.getElementById("logging-dropdown-container");
  loggingDropdownContainer.style.display = showLoggingDropdown ? "flex" : "none";
}

// function setCategorizationVisibility(hideAutoCategorization: boolean) {
//   if (hideAutoCategorization) {
//     document.getElementById("categorization-info-container").style.display = "none";
//     document.getElementById("auto-categorize-checkbox-container").style.display = "none";
//     document.getElementById("update-category-button-container").style.display = "none";
//   }
// }
